import React, { useEffect, useState } from 'react';
import { storedViewAs, setViewAs } from '../lib/viewAs';
import './ViewAsBar.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Admin-only control to render the app as a given user or contractor, plus the
 * banner shown while doing so. The banner is deliberately loud: it should never
 * be possible to forget you are looking at someone else's view.
 */
/**
 * `mode` splits what used to be one floating control into two places, because
 * the two halves are wanted in different ones.
 *
 *   'panel'    the list of people, rendered inline with no trigger of its own.
 *              This is what the account menu drops in under "View as…": the
 *              menu is already a menu, and a dropdown opening out of a
 *              dropdown is two panels fighting over the same corner. The menu
 *              decides when it is shown, so here it is always expanded.
 *   'banner'   the loud "Viewing as X" strip, left exactly where it was: it has
 *              to be impossible to miss, and moving it into the header is how
 *              somebody forgets whose screen they are reading.
 *
 * Passing neither renders the floating picker and the banner both, which is
 * what the contractor portal needs - it has no header to put either in.
 */
function ViewAsBar({ isAdmin, mode }) {
  // Ungated on purpose. getViewAs() only answers once /auth/me has confirmed
  // the administrator - but while impersonating, /auth/me answers as the person
  // being viewed, who is not one. That combination rendered nothing at all: no
  // banner, no exit, and no way back to your own account short of clearing
  // localStorage by hand. Reading the stored value directly guarantees the way
  // out is always on screen. It grants nothing; the server decides everything.
  const active = storedViewAs();
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState({ users: [], contractors: [] });
  // `loaded` says the lists are real. Without it, "None found" was shown
  // while the two requests were still in flight and after they had failed -
  // which for an administrator on a slow link read as "there are no staff".
  const [loaded, setLoaded] = useState(false);
  const [problem, setProblem] = useState(null);
  // In panel mode the component only exists while the account menu is showing
  // it, so it is expanded by definition - there is no trigger to press.
  const expanded = mode === 'panel' ? true : open;

  useEffect(() => {
    if (!expanded || loaded) return undefined;
    let cancelled = false;
    setProblem(null);
    const read = (url) => fetch(url).then((r) => {
      if (!r.ok) throw new Error(`${r.status}`);
      return r.json();
    });
    Promise.all([read(`${API_BASE}/reference-data`), read(`${API_BASE}/contractors`)])
      .then(([ref, cons]) => {
        if (cancelled) return;
        const contractorRows = Array.isArray(cons) ? cons : (cons?.contractors ?? []);
        setPeople({
          users: Array.isArray(ref?.users) ? ref.users : [],
          contractors: contractorRows.filter((c) => c.availability_status !== 'inactive')
        });
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setProblem('Could not load the list of people. Close and open this again to retry.');
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [expanded, loaded]);

  // A closed and reopened picker asks again after a failure.
  useEffect(() => { if (!expanded && problem) { setLoaded(false); setProblem(null); } }, [expanded, problem]);

  // The two groups of people, which both the floating menu and the inline
  // panel render identically. Pulled out so the panel cannot drift from the
  // menu the moment one of them is edited.
  const groups = (
    <>
      <p className="view-as-note">
        Renders the app as this person sees it. For testing — it changes the
        view, not your permissions.
      </p>

      {!loaded && <span className="view-as-empty" role="status" aria-busy="true">Loading people…</span>}
      {problem && <span className="view-as-empty" role="alert">{problem}</span>}

      <div className="view-as-group">
        <span className="view-as-heading">Staff</span>
        {loaded && !problem && people.users.length === 0 && <span className="view-as-empty">None found</span>}
        {people.users.map((u) => (
          <button
            key={u.id}
            className="view-as-option"
            onClick={() => setViewAs({ kind: 'user', id: u.id, name: u.display_name || u.name || u.email })}
          >
            {u.display_name || u.name || u.email}
            <small>{u.role || 'user'}</small>
          </button>
        ))}
      </div>

      <div className="view-as-group">
        <span className="view-as-heading">Contractors</span>
        {loaded && !problem && people.contractors.length === 0 && <span className="view-as-empty">None found</span>}
        {people.contractors.map((c) => (
          <button
            key={c.id}
            className="view-as-option"
            onClick={() => setViewAs({ kind: 'contractor', id: c.id, name: c.name })}
          >
            {c.name}
            <small>{c.company || c.email}</small>
          </button>
        ))}
      </div>
    </>
  );

  if (active) {
    if (mode === 'panel') return null;
    return (
      <div className="view-as-banner" role="status">
        <span className="view-as-dot" aria-hidden="true" />
        <span>
          Viewing as <strong>{active.name || active.id}</strong>
          {active.kind === 'contractor' ? ' (contractor)' : ' (staff)'}
          {' \u2014 actions are recorded against you'}
        </span>
        <button className="btn btn-small view-as-exit" onClick={() => setViewAs(null)}>
          Back to my view
        </button>
      </div>
    );
  }

  if (!isAdmin || mode === 'banner') return null;

  if (mode === 'panel') {
    return <div className="view-as is-panel"><div className="view-as-menu">{groups}</div></div>;
  }

  return (
    <div className="view-as">
      <button
        type="button"
        className="btn btn-secondary btn-small"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        View as…
      </button>

      {open && <div className="view-as-menu">{groups}</div>}
    </div>
  );
}

export default ViewAsBar;
